import { useEffect, useMemo, useRef } from "react";
import { REMOVABLE_ITEM_CHOICE_DATA } from "../gql/items";

import { useLazyQuery } from "@apollo/client";

import { isRemovableItemType } from "./product.utils";
import { ItemType } from "../types/enums";

const getPartialDentureChoices = (productType: ItemType) => {
  return productType === ItemType.PARTIAL_DENTURE ? true : undefined;
};

const getFullDentureChoices = (productType: ItemType) => {
  return productType === ItemType.FULL_DENTURE ? true : undefined;
};

export const useCreateProduct = (itemTypes: ItemType[]) => {
  const hasQueriedRef = useRef(false);

  const [
    getRemovableItemChoices,
    { data: removableItemChoicesData, loading: removableItemChoicesLoading },
  ] = useLazyQuery(REMOVABLE_ITEM_CHOICE_DATA, {
    fetchPolicy: "network-only",
  });

  const queryVariables = useMemo(() => {
    const reducedItemTypes = [...new Set(itemTypes)];
    return reducedItemTypes.map(itemType => ({
      removableVars: isRemovableItemType(itemType)
        ? {
            where: {
              OR: [
                {
                  fullDenture: {
                    equals: getFullDentureChoices(itemType),
                  },
                },
                {
                  partialDenture: {
                    equals: getPartialDentureChoices(itemType),
                  },
                },
              ],
            },
            where2: {
              OR: [
                {
                  fullDentureTeeth: {
                    equals: getFullDentureChoices(itemType),
                  },
                },
                {
                  partialDentureTeeth: {
                    equals: getPartialDentureChoices(itemType),
                  },
                },
                { gingiva: { equals: true } },
              ],
            },
          }
        : null,
    }));
  }, [itemTypes]);

  useEffect(() => {
    if (!hasQueriedRef.current) {
      const firstRemovableVar = queryVariables.find(vars => vars.removableVars);
      if (firstRemovableVar?.removableVars) {
        getRemovableItemChoices({ variables: firstRemovableVar.removableVars });
        hasQueriedRef.current = true;
      }
    }
  }, [queryVariables]);

  return {
    removableItemChoicesData,
    removableItemChoicesLoading,
  };
};
